/**
 * Client-side percentile helpers for Metric View charts.
 *
 * The backend owns the authoritative pooled P85 (see
 * `portfolioService.getCycleTimeP85ByProduct`), but the Cycle Time scatter
 * card also needs a P85 computed over *exactly the points being rendered*
 * — which changes when the Type filter narrows the visible dots. Computing
 * on the frontend keeps the reference line in sync with the plot without an
 * extra API round-trip.
 */

/**
 * 85th-percentile of the given numeric values using **linear interpolation**
 * between the two adjacent observed values (the same algorithm PostgreSQL's
 * `PERCENTILE_CONT(0.85)` uses, and the one the cycle-time endpoint
 * computes server-side).
 *
 * This is intentionally NOT the nearest-rank method — nearest-rank collapses
 * to the sample maximum for any n ≤ 6, which makes the scatter's reference
 * line disagree with the Cycle Time P85 card (which runs `PERCENTILE_CONT`
 * server-side). See PRODOPSAI-314 for the reproducer.
 *
 * Returns `null` when the input is empty so callers can opt out of rendering
 * a reference line.
 */
export function computeP85(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const position = 0.85 * (sorted.length - 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
}
